import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocationImport } from 'planning-portal-library/src/lib/core/domain/models/location-import.model';
import { LocationType } from 'planning-portal-library/src/lib/core/domain/models/location-type';
import { SelectedSegmentId } from 'planning-portal-library/src/lib/core/domain/models/private-location/selected-building-block-id';
import { ProductActivity } from 'planning-portal-library/src/lib/core/domain/models/product-activity';
import { LocationImportRequest } from 'planning-portal-library/src/lib/core/domain/requests/locations/location-import.request';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import { catchError, map, mergeMap, Observable, reduce } from 'rxjs';
import {
  AddManualLocationRequest,
  Filter,
  PostalCode,
  PrivateLocatioDetails,
  PublicLocation,
  PublicLocationRequest,
  Tag,
} from '../../../../core/domain';
import { MasterlocationRepository } from '../../../../core/repositories';
import { MasterLocationConstants } from '../../../shared/application-constants/master-location-constants';
import { BaseResponse } from '../../../shared/base.response';
import {
  PostalCodeWebRepositoryMapper,
  PrivateLocatioDetailsWebEntity,
  PrivateLocationDetailMapper,
  TagsWebRepositoryMapper,
} from './entities';
import { LocationImportWebEntity } from './entities/location-import.web-entity';
import { LocationImportMapper } from './entities/location-import.web-repository-mapper';
import { LocationTypeWebEntity } from './entities/location-type-web-entity';
import { LocationTypeWebRepositoryMapper } from './entities/location-type-web-repository-mapper';
import { ProductActivityWebEntity } from './entities/product-activity-web-entity';
import { ProductActivityWebRepositoryMapper } from './entities/product-activity-web-repository-mapper';
import { PublicLocationWebEntity } from './entities/public-location.web-entity';
import { PublicLocationMapper } from './entities/public-location.web-repository-mapper';
import { SelectedSegmentIdWebRepositoryMapper } from './entities/selected-building-block-id.web-repositor-mapper';
import { MasterLocationApiUrls } from './master-location-api-urls';

@Injectable({
  providedIn: 'root',
})
export class MasterLocationWebRepository extends MasterlocationRepository {
  private readonly privateLocationDetailMapper = new PrivateLocationDetailMapper();
  private readonly postalCodeMapper = new PostalCodeWebRepositoryMapper();
  private readonly tagMapper = new TagsWebRepositoryMapper();
  private readonly locationTypeWebRepositoryMapper =
    new LocationTypeWebRepositoryMapper();
  private readonly publicLocationMapper = new PublicLocationMapper();
  private readonly locationImportMapper = new LocationImportMapper();
  private readonly selectedSegmentIdMapper = new SelectedSegmentIdWebRepositoryMapper();
  private readonly productActivityMapper = new ProductActivityWebRepositoryMapper();
  private readonly environment = injectEnvironment();

  constructor(private readonly http: HttpClient) {
    super();
  }
  getPostalCodes(): Observable<PostalCode[]> {
    return this.http
      .get<BaseResponse<PostalCode[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetPostalCodes}`,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap((response) =>
          response.result!.sort((a, b) =>
            a.postalCode.toLowerCase() < b.postalCode.toLowerCase() ? -1 : 1
          )
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.postalCodeMapper.mapFrom))
      .pipe(reduce((items: PostalCode[], item: PostalCode) => [...items, item], []));
  }

  getFilters(): Observable<Filter[]> {
    return this.http
      .get<BaseResponse<Filter[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetMLSFilters}`,
        
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<Filter[]>) =>
            response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(
        reduce(
          (items: Filter[], item: Filter) => [...items, item],
          []
        )
      );
  }
  getTags(): Observable<Tag[]> {
    return this.http
      .get<BaseResponse<Tag[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetTagsLookup}`,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap((response) =>
          response.result!.sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
          )
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.tagMapper.mapFrom))
      .pipe(reduce((items: Tag[], item: Tag) => [...items, item], []));
  }
  getSegmentIds(): Observable<SelectedSegmentId[]> {
    return this.http
      .get<BaseResponse<SelectedSegmentId[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetSegments}`,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap((response) =>
          response.result!.sort((a, b) =>
            a.segment.toLowerCase() < b.segment.toLowerCase() ? -1 : 1
          )
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.selectedSegmentIdMapper.mapFrom))
      .pipe(
        reduce(
          (items: SelectedSegmentId[], item: SelectedSegmentId) => [
            ...items,
            item,
          ],
          []
        )
      );
  }
  getPublicLocations(
    request: PublicLocationRequest
  ): Observable<PublicLocation[]> {
    return this.http
      .post<BaseResponse<PublicLocationWebEntity[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetPublicLocations}`,
        request,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<PublicLocationWebEntity[]>) =>
            response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.publicLocationMapper.mapFrom))
      .pipe(
        reduce(
          (items: PublicLocation[], item: PublicLocation) => [...items, item],
          []
        )
      );
  }

  getPublicLocationsForAutoComplete(
    request: PublicLocationRequest
  ): Observable<PublicLocation[]> {
    return this.http
      .post<BaseResponse<PublicLocationWebEntity[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetPrivateLocationsForAutoComplete}`,
        request,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap((response: BaseResponse<PublicLocationWebEntity[]>) =>
          response.result!.sort((a, b) => (a.name < b.name ? -1 : 1))
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.publicLocationMapper.mapFrom))
      .pipe(
        reduce(
          (items: PublicLocation[], item: PublicLocation) => [...items, item],
          []
        )
      );
  }

  getLocationImport(
    request: LocationImportRequest
  ): Observable<LocationImport[]> {
    return this.http
      .post<BaseResponse<LocationImportWebEntity[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetPublicLocationsForPP}`,
        request,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<LocationImportWebEntity[]>) =>
            response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.locationImportMapper.mapFrom))
      .pipe(
        reduce(
          (items: LocationImport[], item: LocationImport) => [...items, item],
          []
        )
      );
  }

  getPrivateLocationsDetails(
    request: string
  ): Observable<PrivateLocatioDetails> {
    return this.http
      .get<BaseResponse<PrivateLocatioDetailsWebEntity>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetPrivateLocationDetails}`,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
          params: {
            mlsId: request,
          },
        }
      )
      .pipe(
        map(
          (response: BaseResponse<PrivateLocatioDetailsWebEntity>) =>
            response.result!
        ),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.privateLocationDetailMapper.mapFrom));
  }
  importPlanContainerLocationCSV(request: FormData): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${MasterLocationApiUrls.ImportPlanContainerLocations}`,
        request,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  deleteMasterLocation(request: string): Observable<boolean> {
    return this.http
      .delete<BaseResponse<unknown>>(
        `${this.environment.url}${MasterLocationApiUrls.DeleteMasterLocation}`,
        {
          params: {
            locationId: request,
          },
        }
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  override addManualLocation(
    request: AddManualLocationRequest
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.environment.url}${MasterLocationApiUrls.AddNewPlanLocation}`,
        request,
        {
          headers: {
            ApiKey: MasterLocationConstants.API_KEY_VALUE,
          },
        }
      )
      .pipe(
        map((data: boolean) => {
          return data;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  override getLocationTypes(): Observable<LocationType[]> {
    return this.http
      .get<BaseResponse<LocationTypeWebEntity[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetLocationTypes}`
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<LocationTypeWebEntity[]>) => response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.locationTypeWebRepositoryMapper.mapFrom))
      .pipe(
        reduce(
          (items: LocationType[], item: LocationType) => [...items, item],
          []
        )
      );
  }

  override getActivitiesByProduct(
    productId: string
  ): Observable<ProductActivity[]> {
    return this.http
      .get<BaseResponse<ProductActivityWebEntity[]>>(
        `${this.environment.masterLocationUrl}${MasterLocationApiUrls.GetActivitiesByProduct}`,
        {
          params: { productId: productId },
        }
      )
      .pipe(
        mergeMap(
          (response: BaseResponse<ProductActivityWebEntity[]>) =>
            response.result!
        ),
        catchError((err: HttpErrorResponse) => {
          throw err;
        })
      )
      .pipe(map(this.productActivityMapper.mapFrom))
      .pipe(
        reduce(
          (items: ProductActivity[], item: ProductActivity) => [...items, item],
          []
        )
      );
  }
}
